import reduceByKey from 'utils/reduceByKey'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

function getInitialState() {
  const params = new URLSearchParams(window.location.search)
  return params.get('state') || 'published'
}

interface RfpDetailResponse extends RequestForProposalDetail {
  lanes: Lane[]
}

interface State {
  list: Record<number, RequestForProposal>
  detail: {
    rfp?: RequestForProposalDetail
    meta: {
      error: string | null
      isLoading: boolean
      isExporting: boolean
    }
  }
  state: string
  meta: {
    error: string | null
    isLoading: boolean
  }
}

const initialState: State = {
  list: {},
  detail: {
    meta: {
      error: null,
      isLoading: false,
      isExporting: false,
    },
  },
  state: getInitialState(),
  meta: {
    error: null,
    isLoading: false,
  },
}

export const slice = createSlice({
  name: 'requestForProposalList',
  initialState,
  reducers: {
    fetchRFPListStart(state, action: PayloadAction<string>) {
      state.state = action.payload
      state.meta.isLoading = true
      state.meta.error = null
    },
    fetchRFPListSuccess(state, action: PayloadAction<RequestForProposal[]>) {
      const rfps = action.payload.reduce(
        (acc: Record<number, RequestForProposal>, curr: RequestForProposal) => {
          acc[curr.id] = {
            ...curr,
            meta: {
              error: null,
              isUpdating: false,
            },
          }
          return acc
        },
        {}
      )
      state.list = rfps
      state.meta.isLoading = false
    },
    fetchRFPListFailed(state, action: PayloadAction<string>) {
      state.meta.error = action.payload
      state.meta.isLoading = false
    },
    updateRFPItemStart(state, action: PayloadAction<number>) {
      state.list[action.payload].meta.isUpdating = true
      state.list[action.payload].meta.error = null
    },
    updateRFPItemSuccess(state, action: PayloadAction<RequestForProposal>) {
      state.list[action.payload.id] = {
        ...state.list[action.payload.id],
        ...action.payload,
      }
      state.list[action.payload.id].meta.isUpdating = false
    },
    deleteRFPItemSuccess(state, action: PayloadAction<number>) {
      delete state.list[action.payload]
    },
    updateRFPItemFailed(
      state,
      action: PayloadAction<{ id: number; error: string }>
    ) {
      state.list[action.payload.id].meta.isUpdating = false
      state.list[action.payload.id].meta.error = action.payload.error
    },
    fetchRFPDetailStart(state) {
      state.detail.meta.isLoading = true
      state.detail.meta.error = null
    },
    fetchRFPDetailSuccess(state, action: PayloadAction<RfpDetailResponse>) {
      const lanes = reduceByKey(action.payload.lanes, 'id', {}, (lane) => ({
        ...lane,
        proposals: reduceByKey(lane.proposals, 'id'),
      }))
      state.detail.rfp = {
        ...action.payload,
        lanes,
        meta: { isUpdating: false, error: null },
      }
      state.detail.meta.isLoading = false
    },
    fetchRFPDetailFailed(state, action: PayloadAction<string>) {
      state.detail.meta.error = action.payload
      state.detail.meta.isLoading = false
    },
    updateRFPDetailStart(state) {
      if (state.detail.rfp) {
        state.detail.rfp.meta.isUpdating = true
        state.detail.rfp.meta.error = null
      }
    },
    updateRFPDetailSuccess(state, action: PayloadAction<RfpDetailResponse>) {
      const lanes = reduceByKey(action.payload.lanes, 'id')
      state.detail.rfp = {
        ...state.detail.rfp,
        ...action.payload,
        lanes,
        meta: { isUpdating: false, error: null },
      }
    },
    updateRFPDetailFailed(state, action: PayloadAction<string>) {
      if (state.detail.rfp) {
        state.detail.rfp.meta.isUpdating = false
        state.detail.rfp.meta.error = action.payload
      }
    },
    updateRFPDetailLaneProposal(
      state,
      action: PayloadAction<{ laneId: number; proposal: LaneProposal }>
    ) {
      const { laneId, proposal } = action.payload
      if (state.detail.rfp?.lanes[laneId]) {
        const lane = state.detail.rfp.lanes[laneId]

        if (!lane.awarded_proposals) lane.awarded_proposals = {}

        lane.awarded_proposals[proposal.id] = proposal
        state.detail.rfp.lanes[laneId] = lane
      }
    },
    setRFPDetailExporting(state, action: PayloadAction<boolean>) {
      state.detail.meta.isExporting = action.payload
    },
    setRFPDetailError(state, action: PayloadAction<string>) {
      state.detail.meta.error = action.payload
    },
  },
})

export default slice.reducer
